/**
 * Watches for the channel the user just authorized, then starts its import and
 * writes both facts into the assistant conversation.
 *
 * ─── Why a watcher at all ───────────────────────────────────────────────────
 * OAuth does not come back to this tab. `window.open` hands the user to the
 * provider, the provider redirects THAT tab to the app's callback route, and the
 * callback is processed over there. This tab never learns the outcome directly —
 * the only signal it gets is the integrations readmodel gaining a row. So the
 * wizard subscribes and waits for that row to appear.
 *
 * ─── Why the wait is BOUNDED ────────────────────────────────────────────────
 * Because that signal never arrives if the user closes the provider tab or
 * abandons the flow, and from here those are indistinguishable from "still
 * typing their password". An unbounded spinner would claim to know something it
 * cannot. After `CONNECTION_WAIT_MS` the wizard says so and offers to re-check,
 * which is honest and recoverable. A connection that lands late still wins — the
 * subscription stays open and `timedOut` refuses to overwrite a detected one.
 *
 * ─── Why it writes into the conversation ────────────────────────────────────
 * The wizard is a VIEW; the conversation is the record. Posting the connected
 * channel and the import block here — not in the component — is what makes
 * closing the wizard lossless: the floating assistant renders the same blocks
 * from the same messages, with no second copy of the state.
 */

import { v7 as uuidv7 } from 'uuid';
import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { canImportOrderHistory } from '@domains/integrations/shared/state/capabilityGates';
import { OnboardingWizardState } from '../state';
import { OnboardingWizardCommand } from '../commands';
import { buildIntegrationId } from '../integrationId';
import { dispatchAIAssistantCommand } from '@domains/aiAssistant/state/store';
import { seedChannelPickerGreeting } from '@domains/aiAssistant/state/onboardingSeed';

/** How long to wait for the OAuth round-trip before offering a re-check. */
export const CONNECTION_WAIT_MS = 3 * 60 * 1000;

/** `operationName` the channel publication import runs under. */
const CHANNEL_IMPORT_OPERATION = 'ChannelPublicationImport';
/** `operationName` the historical ORDER seeding runs under. Its own kind, its own run. */
const CHANNEL_ORDER_IMPORT_OPERATION = 'ChannelOrderImport';

/**
 * Write the outcome into the assistant conversation.
 *
 * This is what makes the wizard closable without loss: the transcript holds the
 * connected channel and, when there is one, the live progress block — so the
 * floating panel shows the same thing from the same message.
 */
const seedConversation = (
  label: string,
  publicationsRunId: string | null,
  ordersRunId: string | null
) => {
  const timestamp = Date.now();
  dispatchAIAssistantCommand(
    {
      type: 'aiAssistant/message/addAssistant',
      payload: {
        id: `msg-onboarding-connected-${timestamp}`,
        timestamp,
        content: publicationsRunId
          ? `Listo, ${label} quedó conectado. Estoy trayendo tus publicaciones — podés seguir mientras tanto.`
          : `Listo, ${label} quedó conectado.`,
        // `kind` is stated rather than left to default. A transcript is read back
        // months later; a block that says which import it follows does not depend
        // on a reader (or a future default) guessing right.
        ...( publicationsRunId
          ? { block: { type: 'import-progress', payload: {
            batchProcessId: publicationsRunId,
            channelLabel: label,
            kind: 'publications'
          }}}
          : {})
      }
    },
    { source: 'application' }
  );

  if ( ordersRunId ) seedOrderConversation( label, ordersRunId );
};

/**
 * The orders run gets its OWN message.
 *
 * A message carries one block, and the two runs are independent — the order
 * seeding can start minutes after the publications one (the user has to ask for
 * it) or fail on its own. Folding both into one message would mean either a
 * message that lies about the second run's timing, or one that has to be edited
 * in place. Two messages is what actually happened.
 */
const seedOrderConversation = ( label: string, batchProcessId: string ) => {
  const timestamp = Date.now();
  dispatchAIAssistantCommand(
    {
      type: 'aiAssistant/message/addAssistant',
      payload: {
        id: `msg-onboarding-orders-${timestamp}`,
        timestamp,
        content: `También estoy trayendo el historial de pedidos de ${label}.`,
        block: { type: 'import-progress', payload: {
          batchProcessId,
          channelLabel: label,
          kind: 'orders'
        }}
      }
    },
    { source: 'application' }
  );
};

type IntegrationRow = {
  id: string;
  connectorKind?: string;
  connectorId?: string;
  /** Present on every row — `SystemIntegrationSchema` declares it required
   *  (`systemsIntegrations.types.ts:208`). Optional here only because this local
   *  type describes what the subscription hands over, unvalidated. */
  integrationUserId?: string;
  label?: string;
};

const isSalesChannel = ( row: IntegrationRow ): boolean =>
  ( row.connectorKind ?? '' ).toLowerCase() === 'saleschannel';

/**
 * Find an already-running batch process of `operationName` belonging to this
 * integration, or `null`.
 *
 * ─── A batch process does NOT carry an `integrationId` ───────────────────────
 * Its payload carries `connectorId` and `integrationUserId` SEPARATELY
 * (`batchProcesses.types.ts:105-112`), and the integration row's own id is the
 * pair joined — with an UNDERSCORE, which is why this joins through
 * `buildIntegrationId` rather than interpolating the template again. The
 * predicate has been wrong twice: first matching on a `data.integrationId` that
 * does not exist, then joining with a hyphen on the strength of a docstring in
 * `integrations.aggregate.ts` that was itself wrong (now corrected there). Both
 * times the result was identical and silent — no run ever matched, so a wizard
 * resumed mid-import re-seeded the conversation with no progress card.
 *
 * Swallows its own failure: a channel is connected whether or not we can find
 * its run, and the caller reports "no run attached" honestly rather than
 * bubbling a query error out of a progress widget.
 */
const findRunFor = async (
  backend: ClientLibraryType,
  operationName: string,
  integrationId: string
): Promise<string | null> => {
  try {
    const runs = await backend.queries.BatchProcesses({
      filter: [{ field: 'operationName', op: 'eq', value: operationName }],
      sort: [{ field: 'createdAt', direction: 'desc' }],
      limit: 10
    } as never );
    const match = ( runs ?? []).find(( run ) => {
      const data = run.data as { connectorId?: string; integrationUserId?: string } | undefined;
      // Without this guard a malformed row builds `undefined_undefined`, which
      // would match a second malformed row and adopt a run at random.
      if ( !data?.connectorId || !data.integrationUserId ) return false;
      return buildIntegrationId( data.connectorId, data.integrationUserId ) === integrationId;
    });
    return match?.id ?? null;
  } catch {
    return null;
  }
};

export const createConnectionWatchPolicy = (
  backend: ClientLibraryType
): Policy<OnboardingWizardState, OnboardingWizardCommand> => {
  /** Ids present before the wait began — anything already connected is not news. */
  let knownIds = new Set<string>();
  let rows = new Map<string, IntegrationRow>();
  let subscription: { unsubscribe?: () => void } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let watchingConnectorId: string | null = null;
  let importStartedFor: string | null = null;
  /** Guards the one-shot "what was already here?" evaluation. */
  let preexistingChecked = false;

  const clearTimer = () => {
    if ( timer ) {
      clearTimeout( timer );
      timer = null;
    }
  };

  /**
   * Start the historical ORDER seeding, once, for the connected channel.
   *
   * ─── Its own try/catch, on purpose ──────────────────────────────────────
   * This is a SEPARATE batch process from the publications import, with its own
   * failure mode: only connectors whose client can count what it is about to
   * pull accept it, and the rest are rejected outright with
   * `BATCH_PROCESSES_CHANNEL_CAPABILITY_UNSUPPORTED`. Sharing a `try` with the
   * publications start would let an order seeding the connector refuses erase a
   * publications run that is going perfectly well — and the two are reported
   * separately on screen precisely because either can fail alone.
   *
   * The connector gate is `canImportOrderHistory`, the same one the Integrations
   * modal uses to decide whether to OFFER the checkbox — not a hardcoded
   * connector id, so a second connector implementing the count is a one-line
   * change in `capabilityGates.ts` and nothing here.
   */
  const startOrderImport = async (
    next: OnboardingWizardState,
    ctx: { dispatch: ( command: OnboardingWizardCommand, meta: { source: string }) => void }
  ) => {
    const connectorId = next.connectedConnectorId;
    const integrationUserId = next.connectedIntegrationUserId;
    const label = next.connectedLabel || next.connectedIntegrationId || '';
    const batchProcessId = uuidv7();

    let started = false;
    if ( connectorId && integrationUserId && canImportOrderHistory( connectorId )) {
      // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- the channel is connected either way; the outcome is reported as `orderImport/unavailable` rather than as a connection failure.
      try {
        // No date window, by design: seeding imports the channel's FULL past
        // order history. See `ChannelOrderImportBatchProcessStartedEventSchema`.
        await backend.commands.StartChannelOrderImportBatchProcess({
          id: batchProcessId,
          body: { connectorId, integrationUserId }
        });
        started = true;
      } catch {
        // intentionally empty — reported below
      }
    }

    if ( started ) {
      ctx.dispatch(
        { type: 'onboardingWizard/orderImport/started', payload: { batchProcessId }},
        { source: 'api' }
      );
      seedOrderConversation( label, batchProcessId );
      return;
    }

    ctx.dispatch(
      { type: 'onboardingWizard/orderImport/unavailable', payload: {}},
      { source: 'api' }
    );
  };

  return {
    name: 'onboarding-wizard-connection-watch',

    init: ({ dispatch }) => {
      const handleRows = () => {
        if ( !watchingConnectorId ) return;

        const match = [...rows.values()].find(
          ( row ) =>
            isSalesChannel( row ) &&
            row.connectorId === watchingConnectorId &&
            !knownIds.has( row.id )
        );
        if ( !match ) return;

        clearTimer();
        const label = match.label || match.connectorId || match.id;
        watchingConnectorId = null;

        dispatch(
          { type: 'onboardingWizard/connect/detected', payload: {
            integrationId: match.id,
            connectorId: match.connectorId ?? '',
            // Read off the ROW, not split out of `match.id`. The row declares it,
            // and re-deriving one half of the id from the other is precisely how
            // the separator bug got in.
            integrationUserId: match.integrationUserId ?? '',
            label
          }},
          { source: 'api' }
        );
      };

      const sub = backend.subscriptions.ExternalSystemsIntegrations(
        {},
        ( change: any ) => {
          if ( change?.type === 'snapshot' ) {
            rows = new Map(
              ( change.data?.items ?? []).map(( item: IntegrationRow ) => [ item.id, item ])
            );
            // The snapshot is the BASELINE while idle. Taking it as "already
            // known" is what stops a pre-existing channel from being reported as
            // the one the user just connected.
            if ( !watchingConnectorId ) knownIds = new Set( rows.keys());
            handleRows();
            // The wizard may already be open and waiting on this very snapshot
            // to decide which screen it belongs on; `shouldReact` only fires on
            // a state change, so nudge it.
            dispatch(
              { type: 'onboardingWizard/ui/snapshotReady', payload: {}},
              { source: 'api' }
            );
            return;
          }
          if ( change?.type === 'signal' ) return;

          if ( change?.type === 'delete' ) {
            if ( change.previous?.id ) rows.delete( change.previous.id );
          } else if ( change?.data?.id ) {
            rows.set( change.data.id, change.data );
          }
          handleRows();
        }
      );
      subscription = sub as { unsubscribe?: () => void };

      return () => {
        clearTimer();
        subscription?.unsubscribe?.();
        subscription = null;
      };
    },

    shouldReact: ( prev, next ) =>
      ( prev.open !== next.open ) ||
      ( prev.integrationsSnapshotReady !== next.integrationsSnapshotReady ) ||
      ( prev.connectionWait !== next.connectionWait ) ||
      ( prev.connectedIntegrationId !== next.connectedIntegrationId ) ||
      // The order seeding is user-initiated and lands long after the connection,
      // so it needs its own trigger — nothing else in this list moves when the
      // user presses "traer también mis pedidos".
      ( prev.orderImportRequested !== next.orderImportRequested ),

    onStateChange: async ({ prev, next, ctx }) => {
      /**
       * ── The user asked for the historical ORDER seeding ───────────────────
       *
       * Handled FIRST because nothing else in this method moves on this
       * transition: the channel connected a while ago, so every branch below
       * (which all key off `connectionWait` or `connectedIntegrationId`) would
       * fall straight through to the `justConnected` guard and return.
       *
       * ── Why this is a request and not something the wizard just does ──────
       * Seeding pulls the seller's ENTIRE past order history. The Integrations
       * connect modal defaults its equivalent checkbox to OFF for that exact
       * reason, while defaulting publications ON — a deliberate asymmetry. The
       * wizard has no checkbox, and a first-run screen is the least informed
       * moment in the product to make that call on someone's behalf, so it asks
       * and reacts here instead of firing on connect.
       */
      if ( next.orderImportRequested && !prev.orderImportRequested ) {
        await startOrderImport( next, ctx );
        return;
      }

      /**
       * ── Did the account already have a channel? ───────────────────────────
       *
       * Signing up with Mercado Libre connects the channel and starts the
       * catalog import as part of that first sign-in, before this screen ever
       * renders. Such a user must not be asked to pick a channel — the wizard
       * opens straight on the progress step.
       *
       * ⚠️ Waits for `snapshotSeen`. Deciding on an empty `rows` before the
       * subscription's first delivery would read "no channels" from "not loaded
       * yet" and show the picker to exactly the users this branch exists for —
       * on a slow connection, every time.
       */
      if ( next.open && next.integrationsSnapshotReady && !preexistingChecked && !next.connectedIntegrationId ) {
        preexistingChecked = true;
        const existing = [...rows.values()].find( isSalesChannel );
        if ( existing ) {
          ctx.dispatch(
            { type: 'onboardingWizard/connect/preexisting', payload: {
              integrationId: existing.id,
              connectorId: existing.connectorId ?? '',
              integrationUserId: existing.integrationUserId ?? '',
              label: existing.label || existing.connectorId || existing.id
            }},
            { source: 'api' }
          );
          return;
        }

        /**
         * Nothing connected yet, and now we KNOW it — so this is the account
         * that should be asked which channel it sells on. Seeding here rather
         * than in the Layout gate is the whole point: the gate fires before the
         * snapshot, so it would ask that question of a Mercado-Libre sign-up
         * too, about a channel already connected.
         */
        seedChannelPickerGreeting();
      }

      // ── Arm the wait ──────────────────────────────────────────────────────
      if ( next.connectionWait === 'waiting' && prev.connectionWait !== 'waiting' ) {
        watchingConnectorId = next.selectedConnectorId;
        knownIds = new Set( rows.keys());
        clearTimer();
        timer = setTimeout(() => {
          ctx.dispatch(
            { type: 'onboardingWizard/connect/timedOut', payload: {}},
            { source: 'application' }
          );
        }, CONNECTION_WAIT_MS );
        return;
      }

      /**
       * ── The wait ended without a detection ────────────────────────────────
       * Cancelled by the user, or failed outright. Both must release the timer
       * AND stop watching: a live `watchingConnectorId` would otherwise pick up
       * the NEXT channel of that connector — including one connected minutes
       * later from Integrations — and report it as the outcome of an attempt the
       * user already walked away from.
       */
      if ( prev.connectionWait === 'waiting' && next.connectionWait === 'idle' &&
           !next.connectedIntegrationId ) {
        clearTimer();
        watchingConnectorId = null;
        return;
      }

      // ── A channel appeared: import it, and tell the conversation ──────────
      const justConnected =
        next.connectedIntegrationId && prev.connectedIntegrationId !== next.connectedIntegrationId;
      if ( !justConnected ) return;

      const integrationId = next.connectedIntegrationId!;
      // Guard against a re-entry (a remount, a redelivered row) starting a
      // second import of the same channel — an import is not free to repeat.
      if ( importStartedFor === integrationId ) return;
      importStartedFor = integrationId;

      const label = next.connectedLabel || integrationId;

      /**
       * ── The channel was already there (Mercado Libre sign-up) ─────────────
       *
       * Its import was started during sign-in. Starting another one here would
       * import the whole catalog a second time, so this branch LOOKS FOR the
       * run instead of creating one, and the conversation gets the same
       * progress block pointed at it.
       *
       * If no run is found the flow still continues: the channel is connected
       * either way, and claiming an import that does not exist would leave a
       * progress card in the transcript that can never resolve. It DOES say so
       * on screen — `import/unavailable` — because rendering nothing at all is
       * the bug this branch was reported for.
       */
      if ( next.connectionOrigin === 'preexisting' ) {
        const existingRunId = await findRunFor( backend, CHANNEL_IMPORT_OPERATION, integrationId );

        if ( existingRunId ) {
          ctx.dispatch(
            { type: 'onboardingWizard/import/started', payload: { batchProcessId: existingRunId }},
            { source: 'api' }
          );
        } else {
          ctx.dispatch(
            { type: 'onboardingWizard/import/unavailable', payload: {}},
            { source: 'api' }
          );
        }

        /**
         * An order seeding may ALSO already be in flight — the channel could
         * have been connected from the Integrations modal with the box ticked.
         * Reattach to it; never START one here. Deciding to pull a full order
         * history is the user's, and this branch never saw them decide.
         *
         * Gated on `canImportOrderHistory` so the query is not spent on the
         * connectors that structurally cannot produce such a run.
         */
        const existingOrderRunId =
          canImportOrderHistory( next.connectedConnectorId ?? undefined )
            ? await findRunFor( backend, CHANNEL_ORDER_IMPORT_OPERATION, integrationId )
            : null;

        if ( existingOrderRunId ) {
          ctx.dispatch(
            { type: 'onboardingWizard/orderImport/started', payload: { batchProcessId: existingOrderRunId }},
            { source: 'api' }
          );
        }

        seedConversation( label, existingRunId, existingOrderRunId );
        return;
      }

      /**
       * ── Start the publications import ─────────────────────────────────────
       *
       * The command takes the PAIR — `{ connectorId, integrationUserId }` — per
       * `StartChannelPublicationImportBatchProcessCommandSchema`
       * (`batchProcesses.types.ts:329`, an omit of the started-event schema at
       * `:105-112`). It has never accepted an `integrationId`; sending one is
       * rejected, and because the rejection is swallowed below BY DESIGN the
       * only visible symptom was a progress card that rendered nothing at all.
       * That was the reported bug. The two other call sites — the channels
       * auto-import policy and the sales-channel setup submit policy — have
       * always sent the pair.
       */
      const connectorId = next.connectedConnectorId;
      const integrationUserId = next.connectedIntegrationUserId;

      if ( !connectorId || !integrationUserId ) {
        // The readmodel row declares both, so this is not expected. Reporting it
        // beats sending a body we already know the aggregate will reject — the
        // failure mode that produced the silent card in the first place.
        ctx.dispatch(
          { type: 'onboardingWizard/import/unavailable', payload: {}},
          { source: 'api' }
        );
        seedConversation( label, null, null );
        return;
      }

      const batchProcessId = uuidv7();

      /**
       * No `channelPublicationIds` — that is what "import everything" means on
       * this command, and it is the same call the Integrations screen makes for
       * its own import-all mode.
       */
      let startedImport = false;
      // Deliberate: the channel IS connected even if the import could not be
      // kicked off, so the flow continues and the message below simply omits the
      // progress block. Saying "connection failed" here would be false, and
      // attaching a progress block for a batch process that was never accepted
      // would leave a card in the transcript that can never resolve.
      // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- see above
      try {
        await backend.commands.StartChannelPublicationImportBatchProcess({
          id: batchProcessId,
          body: { connectorId, integrationUserId }
        });
        startedImport = true;

        ctx.dispatch(
          { type: 'onboardingWizard/import/started', payload: { batchProcessId }},
          { source: 'api' }
        );
      } catch {
        // intentionally empty — see the comment above `try`
      }

      if ( !startedImport ) {
        ctx.dispatch(
          { type: 'onboardingWizard/import/unavailable', payload: {}},
          { source: 'api' }
        );
      }

      /**
       * The order seeding is NOT started here — see `orderImport/request`. The
       * wizard offers it on the progress step and this policy reacts to the
       * press.
       */
      seedConversation( label, startedImport ? batchProcessId : null, null );
    }
  };
};
